// The Learn card's number: how many of the reader's blunders and how many
// of their misses in the last 30 days were of each motif family, read from
// the store's facts through the `mistakes.motifs` service (memoised per
// fact by the plugin's setup, as the Blunders and Misses screens do, so a
// mistake is classified once however often the store publishes). The card
// shows the count for the puzzle's kind and family and links it to the
// library's `<kind>-motif-<family>` key, whose games are the ones these
// mistakes are in.
import { aggregatorId, type Accumulator, type Aggregator, type AggregatorId, type GameFacts, type MoveKind } from '@chess/api-model';
import type { MotifFamily, MotifOf } from '@chess/api-mistakes';

export type MotifCounts = Readonly<Record<MoveKind, Partial<Record<MotifFamily, number>>>>;

class CountsAccumulator implements Accumulator<MotifCounts> {
  private readonly counts: Record<MoveKind, Partial<Record<MotifFamily, number>>> = { blunder: {}, miss: {} };
  private readonly motifOf: MotifOf;

  constructor(motifOf: MotifOf) {
    this.motifOf = motifOf;
  }

  add(game: GameFacts): void {
    if (!game.scanned) return;
    for (const m of game.mistakes ?? []) {
      const family = this.motifOf(m, game.colour).family;
      const byFamily = this.counts[m.kind];
      byFamily[family] = (byFamily[family] ?? 0) + 1;
    }
  }

  result(): MotifCounts {
    return this.counts;
  }
}

export class MotifCountsAggregator implements Aggregator<MotifCounts> {
  readonly id: AggregatorId = aggregatorId('puzzle-motif-counts');
  private readonly motifOf: MotifOf;

  /** `motifOf`: the motif classifier, memoised (`memoisedMotifs` over the `mistakes.motifs` service). */
  constructor(motifOf: MotifOf) {
    this.motifOf = motifOf;
  }

  start(): Accumulator<MotifCounts> {
    return new CountsAccumulator(this.motifOf);
  }
}

/** The count for one kind and family, 0 for none. */
export function motifCount(counts: MotifCounts | undefined, kind: MoveKind, family: MotifFamily): number | undefined {
  return counts ? (counts[kind][family] ?? 0) : undefined;
}
